<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import type { PricingData } from "../composables/usePricingData";
import { ref, watch, nextTick } from "vue";
import { Button } from "@/components/ui/button";
import {
    usePaymentGateway,
    billingDetailsSchema,
    BillingDetails,
} from "../composables/usePaymentGateway";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";

import GooglePayLogo from "./logos/GooglePay.svg";
import ApplePayLogo from "./logos/ApplePay.svg";

import { BillingAddress, Plan } from "../types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { CreditCard } from "lucide-vue-next";
import { formatPriceCurrency } from "../utils/formatPriceCurrency";
import { addMonths, addYears } from "date-fns";
import { Badge } from "@/components/ui/badge";
import CountrySelect from "./CountrySelect.vue";
import { useI18n } from "vue-i18n";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { vAutoAnimate } from "@formkit/auto-animate/vue";

const props = defineProps<{
    pricingData: PricingData;
    plans: Plan[];
    clientSecret: string;
    billingAddress: BillingAddress;
    membersCount: number;
    teamId?: number;
}>();

const { format } = useLocalisedTzFormat();
const { locale } = useI18n();

const formSchema = toTypedSchema(billingDetailsSchema);

const prepareName = (address: BillingAddress) => {
    if (!address.first_name || !address.last_name) {
        return "";
    }

    return `${address.first_name} ${address.last_name}`;
};

const form = useForm<BillingDetails>({
    validationSchema: formSchema,
    initialValues: {
        name: prepareName(props.billingAddress),
        country: props.billingAddress.country,
        postal_code: props.billingAddress.postal_code ?? "",
    },
});

const gateway = usePaymentGateway(props.clientSecret);
const country = ref(props.billingAddress.country);

const opened = ref(false);

watch(opened, (value) => {
    if (value) {
        nextTick(() => {
            gateway.init();
        });
    } else {
        planLocked.value = false;
        selectedPlanCycle.value = "option-one";
    }
});

const selectedPlan = ref<Plan>(props.plans[0]);

const selectedTab = ref("card");
const selectedPlanCycle = ref("option-one");

const planLocked = ref(false);

watch(selectedTab, (value) => {
    if (value === "card") {
        nextTick(() => {
            gateway.init();
        });
    }
});

watch(selectedPlanCycle, (value) => {
    if (value === "option-one") {
        selectedPlan.value = props.plans[0];
    } else {
        selectedPlan.value = props.plans[1];
    }
});

const onSubmit = form.handleSubmit(async (values) => {
    subscribe(values, props.teamId);
});

async function subscribe(billindDetails: BillingDetails, teamId?: number) {
    if (!selectedPlan.value) {
        return;
    }

    const res = await gateway.subscribe(
        selectedPlan.value.plan_id,
        {
            name: billindDetails.name,
            country: billindDetails.country,
            postal_code: billindDetails.postal_code,
        },
        teamId,
    );

    if (!res.ok) {
        console.log(res.stack);
    } else {
        opened.value = false;
    }
}
</script>

<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent class="max-w-3xl">
            <DialogHeader>
                <DialogTitle>{{
                    $t("billing.card.payment_order")
                }}</DialogTitle>
                <DialogDescription> </DialogDescription>
            </DialogHeader>

            <div class="flex w-full flex-col gap-4 sm:flex-row-reverse">
                <div
                    class="rounded-lg border border-iron-100 bg-iron-50 p-4 sm:w-1/2"
                >
                    <h2 class="font-semibold">
                        {{ $t("billing.card.summary") }}
                    </h2>

                    <div class="mt-4 rounded-lg">
                        <p class="text-lg font-medium">
                            {{ selectedPlan.name }}
                        </p>
                        <p class="text-iron-950 mt-1">
                            {{
                                formatPriceCurrency(
                                    selectedPlan.price,
                                    selectedPlan.currency,
                                )
                            }}/{{
                                selectedPlan.recurring === "monthly"
                                    ? $t("billing.card.monthly")
                                    : $t("billing.card.yearly")
                            }}
                            <span
                                v-if="membersCount > 1"
                                class="ml-1 mt-1 text-xs text-iron-700"
                            >
                                {{
                                    formatPriceCurrency(
                                        selectedPlan.price * membersCount,
                                        selectedPlan.currency,
                                    )
                                }}
                                for {{ membersCount }} members
                            </span>
                        </p>
                        <p class="mt-1 text-sm text-iron-600">
                            {{ $t("billing.card.auto_renewal_date") }}
                            {{
                                selectedPlan.recurring === "monthly"
                                    ? format(addMonths(new Date(), 1), "PPP")
                                    : format(addYears(new Date(), 1), "PPP")
                            }}
                        </p>
                    </div>

                    <hr class="mt-8" />

                    <div class="mt-2 flex justify-between">
                        <p class="text-xl font-medium">
                            {{ $t("billing.card.total") }}
                        </p>
                        <p class="text-xl font-medium">
                            {{
                                formatPriceCurrency(
                                    selectedPlan.price * membersCount,
                                    selectedPlan.currency,
                                )
                            }}
                        </p>
                    </div>

                    <p class="mt-1 text-sm text-iron-500">
                        {{ $t("billing.card.auto_renewal_notice") }}
                    </p>
                </div>

                <div v-show="planLocked" class="mt-4 sm:mt-0 sm:w-1/2">
                    <Tabs
                        v-model:model-value="selectedTab"
                        default-value="card"
                    >
                        <TabsList>
                            <TabsTrigger value="card">
                                <CreditCard
                                    :size="24"
                                    :stroke-width="1.75"
                                    absolute-stroke-width
                                />
                            </TabsTrigger>
                            <!-- <TabsTrigger value="apple-pay">
                                <ApplePayLogo class="h-5 w-10" />
                            </TabsTrigger>
                            <TabsTrigger value="google-pay">
                                <GooglePayLogo class="h-5 w-10" />
                            </TabsTrigger> -->
                        </TabsList>
                        <TabsContent value="card">
                            <form
                                id="payment-form"
                                class="mt-4"
                                @submit.prevent="onSubmit"
                            >
                                <div class="form-row mt-2">
                                    <FormField
                                        v-slot="{ componentField }"
                                        name="name"
                                    >
                                        <FormItem v-auto-animate>
                                            <FormLabel>{{
                                                $t("billing.card.full_name")
                                            }}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="text"
                                                    :placeholder="
                                                        $t(
                                                            'billing.card.full_name',
                                                        )
                                                    "
                                                    v-bind="componentField"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    </FormField>

                                    <div class="mt-2 grid grid-cols-2 gap-3">
                                        <div>
                                            <CountrySelect v-model="country" />
                                        </div>
                                        <div class="grid items-center gap-1.5">
                                            <FormField
                                                v-slot="{ componentField }"
                                                name="postal_code"
                                            >
                                                <FormItem v-auto-animate>
                                                    <FormLabel>{{
                                                        $t(
                                                            "billing.billing_information.zip",
                                                        )
                                                    }}</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="text"
                                                            :placeholder="
                                                                $t(
                                                                    'billing.billing_information.zip',
                                                                )
                                                            "
                                                            v-bind="
                                                                componentField
                                                            "
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            </FormField>
                                        </div>
                                    </div>

                                    <div class="mt-8 w-full">
                                        <Label class="mb-2" for="postal_code">{{
                                            $t("billing.card.card_details")
                                        }}</Label>

                                        <div
                                            id="card-number"
                                            class="h-[43px] w-full rounded-lg border border-iron-200 p-3"
                                        >
                                            <!-- A Stripe Element will be inserted here. -->
                                        </div>

                                        <div
                                            class="mt-3 grid grid-cols-2 gap-3"
                                        >
                                            <div
                                                id="card-expiry"
                                                class="col-span-1 rounded-lg border border-iron-200 p-3"
                                            >
                                                <!-- A Stripe Element will be inserted here. -->
                                            </div>

                                            <div
                                                id="card-cvc"
                                                class="col-span-1 rounded-lg border border-iron-200 p-3"
                                            >
                                                <!-- A Stripe Element will be inserted here. -->
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Used to display form errors. -->
                                    <div id="card-errors" role="alert"></div>
                                </div>

                                <div class="mt-6 flex justify-end sm:mt-4">
                                    <Button
                                        type="submit"
                                        class="w-full"
                                        size="lg"
                                    >
                                        {{ $t("billing.card.pay") }}
                                    </Button>
                                </div>
                            </form>
                        </TabsContent>
                        <TabsContent value="apple-pay">
                            Apple pay TBD
                        </TabsContent>
                        <TabsContent value="google-pay">
                            Google pay TBD
                        </TabsContent>
                    </Tabs>
                </div>

                <div
                    v-show="!planLocked"
                    class="mt-4 flex flex-col justify-end sm:mt-0 sm:w-1/2"
                >
                    <RadioGroup
                        v-model:model-value="selectedPlanCycle"
                        default-value="option-one"
                    >
                        <div
                            class="flex items-center justify-between rounded-xl border"
                            :class="[
                                selectedPlanCycle === 'option-one'
                                    ? 'border border-iron-500'
                                    : 'border-iron-200',
                            ]"
                        >
                            <Label
                                class="flex w-full items-center justify-between py-3.5 pl-4"
                                for="option-one"
                            >
                                <div class="flex gap-1.5">
                                    <p class="font-medium">
                                        {{ $t("billing.card.yearly_payment") }}
                                    </p>
                                    <Badge :variant="'outline'">
                                        {{
                                            locale === "cs"
                                                ? "sleva 1 200 Kč"
                                                : "$60 off"
                                        }}</Badge
                                    >
                                </div>

                                <RadioGroupItem
                                    id="option-one"
                                    class="mr-4"
                                    value="option-one"
                                >
                                </RadioGroupItem>
                            </Label>
                        </div>
                        <div
                            class="flex items-center justify-between rounded-xl border border-iron-200 px-4 py-3.5"
                            :class="[
                                selectedPlanCycle === 'option-two'
                                    ? 'border border-iron-500'
                                    : 'border-iron-200',
                            ]"
                        >
                            <Label
                                class="flex w-full items-center justify-between"
                            >
                                <p class="font-medium">
                                    {{ $t("billing.card.monthly_payment") }}
                                </p>
                                <RadioGroupItem
                                    id="option-two"
                                    value="option-two"
                                />
                            </Label>
                        </div>
                    </RadioGroup>

                    <Button
                        class="mt-3 h-[60px] text-[16px]"
                        @click="planLocked = true"
                    >
                        {{ $t("billing.card.continue_to_payment") }}</Button
                    >
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<style scoped>
.focused {
    border: 1px solid blue;
    outline: 2px solid black;
    outline-offset: 2px;
}
</style>
